import { SearchableSelect } from "@/components/ui/searchable-select";
import { TidyNotesButton } from "@/components/ai/TidyNotesButton";
import { MentionTextarea } from "@/components/ui/mention-textarea";
import { useEffect, useMemo, useState } from "react";
import { CheckSquare2, Inbox, Mail, MapPin, MessageCircle, Phone, StickyNote, Video } from "lucide-react";
import type { TimelineVisit } from "@/lib/visits";
import { VisitTimelineExtra } from "@/components/visits/VisitTimelineExtra";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { WhatsAppIcon } from "@/components/whatsapp/WhatsAppIcon";
import { useToast } from "@/context/ToastContext";
import { api } from "@/lib/api";
import { unwrapPage } from "@/lib/paging";
import { formatINRCompact } from "@/lib/utils";
import type { LeadInboxItem, LeadRecord } from "@/types";
import type { Task } from "@/types/task";
import type { WhatsAppThread } from "@/types/whatsapp";
import { dateLocale, withClock } from "@/lib/dateTime";

interface ActivityRow {
  id: string;
  activity_type: "NOTE" | "CALL" | "MEETING" | "VISIT";
  title: string | null;
  body: string | null;
  call_outcome: string | null;
  duration_minutes: number | null;
  occurred_at?: string | null;
  visit?: TimelineVisit | null;
  created_on: string;
  created_by?: { name?: string } | string | null;
}

interface Entry {
  key: string;
  at: number;
  icon: React.ElementType;
  tone: string;
  title: string;
  meta?: string;
  body?: string;
  visit?: TimelineVisit | null;
}

const OUTCOME: Record<string, string> = { CONNECTED: "connected", NO_ANSWER: "no answer", BUSY: "busy", VOICEMAIL: "voicemail", WRONG_NUMBER: "wrong number", CALL_BACK: "call back" };
const clock = (at: number) => new Date(at).toLocaleTimeString(dateLocale(), withClock({ hour: "2-digit", minute: "2-digit" }));
const dayLabel = (at: number) => {
  const days = Math.round((new Date().setHours(0, 0, 0, 0) - new Date(at).setHours(0, 0, 0, 0)) / 86400000);
  if (days === 0) return "Today";
  if (days === 1) return "Yesterday";
  const d = new Date(at);
  return d.toLocaleDateString(dateLocale(), { day: "numeric", month: "long", year: d.getFullYear() === new Date().getFullYear() ? undefined : "numeric" });
};

/** What the lead asked for, the box to log something, and everything that has happened, day by day */
export function LeadActivity({
  lead,
  tasks,
  whatsapp,
  inquiries,
  reloadKey,
  onCall,
  onEmail,
  onWhatsApp,
  onTask,
}: {
  lead: LeadRecord;
  tasks: Task[];
  whatsapp: WhatsAppThread[];
  inquiries: LeadInboxItem[];
  reloadKey: number;
  onCall: () => void;
  onEmail: () => void;
  onWhatsApp: () => void;
  onTask: () => void;
}) {
  const { success, error } = useToast();
  const [rows, setRows] = useState<ActivityRow[]>([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [callOpen, setCallOpen] = useState(false);
  const [call, setCall] = useState({ outcome: "CONNECTED", minutes: "", summary: "" });
  const [version, setVersion] = useState(0);
  const [meetingOpen, setMeetingOpen] = useState(false);
  const [meeting, setMeeting] = useState({ title: "", when: "", summary: "" });

  useEffect(() => {
    api
      .get(`/api/CustomerActivity/List?related_type=LEAD&related_id=${lead.id}`)
      .then((r) => setRows(unwrapPage<ActivityRow>(r).rows))
      .catch(() => setRows([]));
  }, [lead.id, version, reloadKey]);

  const saveNote = async () => {
    setBusy(true);
    try {
      await api.post("/api/CustomerActivity/Insert", { related_type: "LEAD", related_id: lead.id, activity_type: "NOTE", body: note.trim() });
      setNote("");
      setVersion((v) => v + 1);
      success("Note saved", "Added to the lead's history");
    } catch (e: any) {
      error("Could not save the note", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const saveCall = async () => {
    setBusy(true);
    try {
      await api.post("/api/CustomerActivity/Insert", {
        related_type: "LEAD",
        related_id: lead.id,
        activity_type: "CALL",
        call_direction: "OUTBOUND",
        call_outcome: call.outcome,
        duration_minutes: call.minutes ? Number(call.minutes) : undefined,
        body: call.summary.trim() || undefined,
        occurred_at: new Date().toISOString(),
      });
      setCallOpen(false);
      setCall({ outcome: "CONNECTED", minutes: "", summary: "" });
      setVersion((v) => v + 1);
      success("Call logged", "Added to the lead's history");
    } catch (e: any) {
      error("Could not log the call", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const saveMeeting = async () => {
    setBusy(true);
    try {
      await api.post("/api/CustomerActivity/Insert", {
        related_type: "LEAD",
        related_id: lead.id,
        activity_type: "MEETING",
        title: meeting.title.trim() || "Meeting",
        body: meeting.summary.trim() || undefined,
        occurred_at: meeting.when ? new Date(meeting.when).toISOString() : new Date().toISOString(),
      });
      setMeetingOpen(false);
      setMeeting({ title: "", when: "", summary: "" });
      setVersion((v) => v + 1);
      success("Meeting logged", "Added to the lead's history");
    } catch (e: any) {
      error("Could not log the meeting", e?.message || "Try again");
    } finally {
      setBusy(false);
    }
  };

  const days = useMemo(() => {
    const list: Entry[] = [];
    for (const a of rows) {
      const at = new Date(a.created_on).getTime();
      if (a.activity_type === "NOTE") list.push({ key: a.id, at, icon: StickyNote, tone: "bg-warning-soft text-warning", title: "Note", body: a.body || undefined });
      else if (a.activity_type === "CALL")
        list.push({
          key: a.id,
          at,
          icon: Phone,
          tone: "bg-accent text-accent-foreground",
          title: `Call · ${OUTCOME[a.call_outcome || ""] || "logged"}${a.duration_minutes ? ` · ${a.duration_minutes} min` : ""}`,
          body: a.body || undefined,
        });
      else if (a.activity_type === "VISIT")
        list.push({ key: a.id, at: a.occurred_at ? new Date(a.occurred_at).getTime() : at, icon: MapPin, tone: "bg-success-soft text-success", title: a.title || "Visit", meta: a.duration_minutes != null ? `${a.duration_minutes} min on site` : undefined, body: a.body || undefined, visit: a.visit });
      else list.push({ key: a.id, at, icon: Video, tone: "bg-info-soft text-info", title: a.title || "Meeting", body: a.body || undefined });
    }
    for (const q of inquiries) {
      const at = new Date(q.received_at).getTime();
      const need = q.normalized?.requirement_notes || q.normalized?.product_interest;
      list.push({ key: `inq-${q.id}`, at, icon: Inbox, tone: "bg-muted text-muted-foreground", title: `New enquiry from ${q.connection?.name || "an intake channel"}`, meta: need ? String(need).slice(0, 90) : q.inbox_status.toLowerCase() });
    }
    for (const t of whatsapp) {
      const last = t.conversation.last_message_at;
      if (last) list.push({ key: `wa-${t.conversation.id}`, at: new Date(last).getTime(), icon: MessageCircle, tone: "bg-success-soft text-success", title: "WhatsApp conversation", meta: t.conversation.last_message_preview || undefined });
    }
    for (const t of tasks) {
      const at = new Date(t.completed_at || t.due_at).getTime();
      list.push({ key: `task-${t.id}`, at, icon: CheckSquare2, tone: "bg-muted text-muted-foreground", title: `${t.task_status === "DONE" ? "Done" : "Task"}: ${t.title}`, meta: t.task_status === "DONE" ? undefined : `Due ${new Date(t.due_at).toLocaleDateString(dateLocale(), { day: "2-digit", month: "short" })}` });
    }
    list.push({ key: "created", at: new Date(lead.created_on as string).getTime(), icon: Inbox, tone: "bg-muted text-muted-foreground", title: "Lead created", meta: lead.source ? `From ${lead.source}` : undefined });
    list.sort((a, b) => b.at - a.at);
    const out: { label: string; items: Entry[] }[] = [];
    for (const e of list) {
      const label = dayLabel(e.at);
      const last = out[out.length - 1];
      if (last && last.label === label) last.items.push(e);
      else out.push({ label, items: [e] });
    }
    return out;
  }, [rows, inquiries, whatsapp, tasks, lead]);

  const tabs = [
    { label: "Note", icon: StickyNote, active: true },
    { label: "Call", icon: Phone, run: () => setCallOpen(true), show: !!(lead.mobile || lead.phone) },
    { label: "Meeting", icon: Video, run: () => setMeetingOpen(true), show: true },
    { label: "Email", icon: Mail, run: onEmail, show: !!lead.email },
    { label: "WhatsApp", icon: WhatsAppIcon, run: onWhatsApp, show: !!(lead.mobile || lead.phone) },
    { label: "Task", icon: CheckSquare2, run: onTask, show: true },
  ].filter((t) => t.show !== false);

  const hasRequirement = lead.requirement_notes || lead.expected_budget || lead.expected_timeline || lead.product_interest;

  return (
    <div className="flex min-w-0 flex-col gap-3">
      {hasRequirement && (
        <section className="flex flex-col gap-2 rounded-lg border border-border bg-card px-4 py-3">
          <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-muted-foreground">Requirement</h2>
          {lead.requirement_notes && <p className="whitespace-pre-wrap text-[13px]">{lead.requirement_notes}</p>}
          <div className="flex flex-wrap gap-2">
            {[
              ["Expected budget", lead.expected_budget ? formatINRCompact(Number(lead.expected_budget)) : null],
              ["Timeline", lead.expected_timeline],
              ["Product interest", lead.product_interest],
            ]
              .filter(([, v]) => v)
              .map(([k, v]) => (
                <span key={k as string} className="flex flex-col rounded-md bg-panel px-2.5 py-1.5">
                  <span className="text-[11px] text-muted-foreground">{k}</span>
                  <span className="text-[13px] font-semibold">{v}</span>
                </span>
              ))}
          </div>
        </section>
      )}

      <div className="flex flex-col rounded-lg border border-border bg-card">
        <div role="group" aria-label="Log activity" className="flex flex-wrap gap-1 border-b border-border/70 px-2.5 py-2">
          {tabs.map((t) => (
            <button
              key={t.label}
              type="button"
              aria-pressed={!!t.active}
              onClick={t.run}
              className={`flex h-[26px] items-center gap-1.5 rounded-md px-2.5 text-xs transition-colors ${t.active ? "bg-sidebar font-semibold text-white" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
            >
              <t.icon className="size-3.5" />
              {t.label}
            </button>
          ))}
        </div>
        <label className="px-3.5 pt-2.5">
          <span className="sr-only">Note</span>
          <MentionTextarea rows={2} value={note} onChange={setNote} placeholder="Write a note about this lead… type @ to mention someone" className="w-full resize-none bg-transparent text-[13px] outline-none placeholder:text-muted-foreground" />
        </label>
        <div className="flex justify-end px-2.5 pb-2.5 pt-1.5">
          <Button size="sm" disabled={busy || !note.trim()} onClick={saveNote}>
            Save note
          </Button>
        </div>
      </div>

      {days.map((d) => (
        <div key={d.label} className="flex flex-col gap-1.5">
          <h3 className="mt-0.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">{d.label}</h3>
          <div className="rounded-lg border border-border bg-card">
            {d.items.map((e, i) => (
              <div key={e.key} className={`flex gap-3 px-3.5 py-2.5 ${i ? "border-t border-border/60" : ""}`}>
                <span className={`flex size-7 shrink-0 items-center justify-center rounded-lg ${e.tone}`}>
                  <e.icon className="size-3.5" />
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                  <div className="flex justify-between gap-2.5">
                    <span className="text-[13px] font-medium">{e.title}</span>
                    <span className="whitespace-nowrap font-mono text-[11.5px] text-muted-foreground">{clock(e.at)}</span>
                  </div>
                  {e.meta && <span className="text-xs text-muted-foreground">{e.meta}</span>}
                  {e.body && <p className="mt-1 whitespace-pre-wrap rounded-md bg-panel px-2.5 py-2 text-[13px]">{e.body}</p>}
                  <VisitTimelineExtra visit={e.visit} label={e.title} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}

      <Dialog open={meetingOpen} onOpenChange={setMeetingOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log a meeting</DialogTitle>
            <DialogDescription>A meeting held or booked with this lead. It is added to their history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <Input placeholder="What the meeting is about" value={meeting.title} onChange={(e) => setMeeting((m) => ({ ...m, title: e.target.value }))} />
            <Input type="datetime-local" value={meeting.when} onChange={(e) => setMeeting((m) => ({ ...m, when: e.target.value }))} />
            <textarea rows={3} value={meeting.summary} onChange={(e) => setMeeting((m) => ({ ...m, summary: e.target.value }))} placeholder="Notes" className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none" />
            <TidyNotesButton text={meeting.summary} kind="MEETING" onTidied={(n) => setMeeting((m) => ({ ...m, summary: n }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMeetingOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={saveMeeting}>
              Log meeting
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={callOpen} onOpenChange={setCallOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Log a call</DialogTitle>
            <DialogDescription>How the call with this lead went. It is added to their history.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="flex gap-2">
              <SearchableSelect value={call.outcome} onChange={(e) => setCall((c) => ({ ...c, outcome: e.target.value }))} className="h-9 flex-1 rounded-md border border-input bg-background px-2 text-sm">
                {Object.entries(OUTCOME).map(([k, v]) => (
                  <option key={k} value={k}>
                    {v.charAt(0).toUpperCase() + v.slice(1)}
                  </option>
                ))}
              </SearchableSelect>
              <Input type="number" min={0} max={600} placeholder="Minutes" className="w-28" value={call.minutes} onChange={(e) => setCall((c) => ({ ...c, minutes: e.target.value }))} />
            </div>
            <textarea rows={3} value={call.summary} onChange={(e) => setCall((c) => ({ ...c, summary: e.target.value }))} placeholder="What was said, what was agreed" className="w-full resize-none rounded-md border border-input bg-background px-3 py-2 text-sm outline-none" />
            <TidyNotesButton text={call.summary} kind="CALL" onTidied={(n) => setCall((c) => ({ ...c, summary: n }))} />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCallOpen(false)}>
              Cancel
            </Button>
            <Button disabled={busy} onClick={saveCall}>
              Log call
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
